import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const alertVariants = cva(
    "relative grid w-full grid-cols-[auto_1fr] items-start gap-x-3 rounded-lg border px-4 py-3 text-sm [&>svg]:mt-0.5 [&>svg]:size-4",
    {
        variants: {
            variant: {
                default: "bg-card text-card-foreground",
                destructive:
                    "border-destructive/40 bg-destructive/5 text-destructive [&_[data-slot=alert-description]]:text-destructive/80",
                success:
                    "border-success/40 bg-success/5 text-success [&_[data-slot=alert-description]]:text-success/80",
                warning:
                    "border-warning/40 bg-warning/5 text-warning [&_[data-slot=alert-description]]:text-warning/80",
                info: "border-info/40 bg-info/5 text-info [&_[data-slot=alert-description]]:text-info/80",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

interface AlertProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof alertVariants> {}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
    ({ className, variant, ...props }, ref) => (
        <div
            ref={ref}
            role="alert"
            className={cn(alertVariants({ variant }), className)}
            {...props}
        />
    ),
);
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
    HTMLHeadingElement,
    React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
    <h3
        ref={ref}
        data-slot="alert-title"
        className={cn("col-start-2 font-medium leading-none", className)}
        {...props}
    />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        data-slot="alert-description"
        className={cn("col-start-2 mt-1 text-muted-foreground", className)}
        {...props}
    />
));
AlertDescription.displayName = "AlertDescription";

const AlertAction = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("col-start-2 mt-3", className)} {...props} />
));
AlertAction.displayName = "AlertAction";

export { Alert, AlertAction, AlertDescription, AlertTitle };
